import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import axios from "axios";
import SafeArea from "components/safe-area";
import FlatContainer from "components/container/flat";
import Table from "components/table";
import { OutlineButton } from "components/button";
import { Status } from "components/status";
import {
  getUnsubscribed,
  unsubscribedCsvUrl,
  UnsubscribedPage,
  UnsubscribedRow,
} from "./api";

const PAGE_SIZE = 25;

/**
 * Who this business can no longer email, and why (ESERV2-1463).
 *
 * Two different facts share this list. A customer who unsubscribed asked us to
 * stop; a suppressed address is one we cannot deliver to at all — usually
 * because it bounced. Both belong here, because the question the office asks
 * is "why isn't this person getting our email", and the answer is the same
 * kind of answer either way.
 *
 * The distinction matters for what can be done about it: an unsubscribe is the
 * customer's decision and only they should reverse it, while a bad address is
 * a data problem the office can fix.
 */
const UnsubscribedPage_ = () => {
  const { t } = useTranslation();
  const tBase = "views.outreach.unsubscribed";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Typing a name should not fire a query per keystroke against a list that
  // can run to thousands of rows.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const { data, isLoading, isError } = useQuery<UnsubscribedPage>(
    ["unsubscribed", debounced, page],
    () =>
      getUnsubscribed({
        search: debounced || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    { keepPreviousData: true },
  );

  const total = data?.total ?? 0;
  const lastPage = Math.max(Math.ceil(total / PAGE_SIZE) - 1, 0);

  const handleExport = async () => {
    // Fetched rather than linked: the request carries the session the same way
    // every other call does, and the server builds the file from the same
    // query, so the export is the whole list and not the page on screen.
    setExporting(true);
    try {
      const res = await axios.get(unsubscribedCsvUrl(debounced || undefined), {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "unsubscribed.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo(
    () => ({
      email: {
        label: tr("colEmail"),
        // normal-case overrides the shared table's `text-transform: capitalize`,
        // which would otherwise render an address as "Someone@Cox.Net". Only the
        // domain is case-insensitive; the local part is not, so displaying a
        // capitalised version of an address is showing something that was never
        // stored and may not be the same address.
        cell: (row: UnsubscribedRow) => (
          <span className="break-all normal-case">{row.email}</span>
        ),
      },
      name: {
        label: tr("colName"),
        cell: (row: UnsubscribedRow) => {
          const name = [row.firstName, row.lastName].filter(Boolean).join(" ");
          return name || row.companyName || "--";
        },
      },
      status: {
        label: tr("colStatus"),
        cell: (row: UnsubscribedRow) => (
          <Status
            color={row.kind === "opted-out" ? "234,179,8" : "249,115,22"}
          >
            {row.kind === "opted-out" ? tr("unsubscribed") : tr("undeliverable")}
          </Status>
        ),
      },
      reason: {
        label: tr("colReason"),
        cell: (row: UnsubscribedRow) => tr(`reason_${row.reason}`) || row.reason,
      },
      at: {
        label: tr("colDate"),
        cell: (row: UnsubscribedRow) =>
          row.at ? new Date(row.at).toLocaleDateString() : "--",
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const tableData = isLoading && !data ? undefined : isError ? [] : data?.rows ?? [];

  return (
    <SafeArea>
      <FlatContainer
        title={<h2>{tr("title")}</h2>}
        buttons={[
          <OutlineButton
            key="export"
            size="small"
            onClick={handleExport}
            disabled={exporting || total === 0}
          >
            {exporting ? tr("exporting") : tr("exportCsv")}
          </OutlineButton>,
        ]}
      >
        <p className="text-sm text-gray-500 mb-4">{tr("explainer")}</p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <input
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-80 focus:outline-none focus:border-brand"
            placeholder={tr("searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="text-sm text-gray-500">
            {tr("totalLabel")}: {total.toLocaleString()}
          </span>
        </div>

        {isError && <p className="text-red-500 py-4">{tr("loadError")}</p>}

        <div className="overflow-x-auto">
          <Table data={tableData} columns={columns} disableSortAll />
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 pt-4">
            <OutlineButton
              size="small"
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
            >
              {tr("previous")}
            </OutlineButton>
            <span className="text-sm text-gray-500">
              {tr("pageLabel")} {page + 1} / {lastPage + 1}
            </span>
            <OutlineButton
              size="small"
              onClick={() => setPage((p) => Math.min(p + 1, lastPage))}
              disabled={page >= lastPage}
            >
              {tr("next")}
            </OutlineButton>
          </div>
        )}
      </FlatContainer>
    </SafeArea>
  );
};

export default UnsubscribedPage_;
